import { Rng } from '../core/rng';
import { clamp, TAU, Vec3, vecToBearing, wrapAngle } from '../core/math';
import { AMMO, SHIP_CLASSES, type AmmoId, type DifficultyId, type ShipClassId } from '../data/classes';
import { MASTS, cannonMount, deckHalfWidth, deckY, halfBreadthXY, sheerY, sternX, type MastId, type MastSpec } from '../data/ship';
import { SECTION_BOUNDS, sectionAt, sectionName } from './damage';
import { Vessel, type Faction } from './vessel';
import { Captain, hostile } from './captain';
import { BOARDING_GAP, GRAPNEL_REACH, GRAPNEL_SPEED, LINE_LENGTH, canGrapple, castOff, hullGap, hullOverlap, lash } from './contact';
import type { Cloth } from './cloth';
import type { SimEvent } from './events';
import type { World } from './world';

// Developer tools: the game's systems put through their paces on request,
// without waiting for them to come up in play. Every tool is a DevAction
// carried by the PlayerCommand, so it reaches the sim like any other order,
// and none of this runs unless asked for. Each reports what it did as a
// 'dev' event. A new scenario is a new action kind here plus a tool
// definition in ui/devPanel.ts.

export type DevFaction = Exclude<Faction, 'player'>;

/**
 * How a ship the tools put on the water is commanded: nobody at all (she
 * holds her course and never fires), about her own business, or told where
 * her enemy is and set to fight, board, ram, run, or hold her course and fire.
 */
export type DevAi = 'off' | 'patrol' | 'fight' | 'board' | 'ram' | 'flee' | 'hold';

/** The state a ship is put on the water in. */
export type DevCondition = 'fresh' | 'battered' | 'crippled' | 'struck' | 'burning' | 'sinking';

export interface SpawnSpec {
  cls: ShipClassId;
  faction: DevFaction;
  /** Where: bearing from our bow (radians, clockwise) and range (m). */
  bearing: number;
  range: number;
  /** Her heading: toward us, away from us, or `turn` from ours. */
  course: 'toward' | 'away' | 'relative';
  turn: number;
  /** Speed through the water, m/s. */
  speed: number;
  /** Sail plan, 0 (furled) .. 4 (all plain sail). */
  plan: number;
  ai: DevAi;
  condition: DevCondition;
  /** Fraction of her company aboard. */
  crew: number;
  count: number;
  /** Several ships: in line ahead or abreast, this far apart (m). */
  formation: 'line' | 'abreast';
  spacing: number;
  difficulty: DifficultyId;
}

/**
 * Boat merging: two ships brought together and made fast (grapnels, the
 * lines hauled in, then a boarding fight across the rails), set up exactly.
 */
export interface MergeSpec {
  /** Us and a new ship, or two new ships ahead of us (a boarder and her mark). */
  pair: 'us' | 'them';
  /** The boarder, when the pair is two new ships. */
  boarderClass: ShipClassId;
  boarderFaction: DevFaction;
  /** The ship to be grappled. */
  cls: ShipClassId;
  faction: DevFaction;
  /** Which side of the boarder she lies (+1 starboard). */
  side: 1 | -1;
  /** Water between the hulls, m. */
  gap: number;
  /** Her centre ahead (+) or astern (-) of the boarder's, m. */
  offset: number;
  /** Her heading off the boarder's, radians (+ turned in toward her). */
  angle: number;
  /** The boarder's speed, m/s (null keeps her way as it is). */
  speed: number | null;
  /** Her speed over the boarder's (m/s, + faster), and closing across the gap. */
  relSpeed: number;
  closing: number;
  state: 'active' | 'struck';
  /** Fraction of her company aboard, and their morale (0..1). */
  crew: number;
  morale: number;
  /** The boarder's morale (0..1), or negative to leave it. */
  boarderMorale: number;
  /** Her captain: none, or one who fights, runs, or boards the boarder herself. */
  ai: 'off' | 'fight' | 'flee' | 'board';
  /** Grapnels: not yet, the ordinary order (the rules apply), or made fast at once. */
  grapple: 'none' | 'order' | 'force';
}

export type MergeOrder = 'grapple' | 'order' | 'cut' | 'strike' | 'capture' | 'weaken' | 'rally' | 'dishearten' | 'tear';

export type AimPoint = 'waterline' | 'hull' | 'rail' | 'deck' | 'mast' | 'rigging' | 'rudder';

export interface FireSpec {
  target: number;
  ammo: AmmoId;
  /** Shot weight relative to a 12-pounder. */
  weight: number;
  aim: AimPoint;
  /** Along her: hull section 0 (stern) .. 4 (bow). */
  section: number;
  /** Where the gun is: bearing from her bow (radians, clockwise), and range (m). */
  from: number;
  range: number;
  count: number;
  /** Seconds between rounds (0: all at once). */
  interval: number;
  /** The gun's height above the sea, m: a gun deck is 4, a fighting top 20; higher plunges onto her deck. */
  height: number;
}

export type CollideScenario = 'ramUs' | 'glancing' | 'headOn' | 'weRam' | 'pair' | 'bump';

export interface CollideSpec {
  scenario: CollideScenario;
  cls: ShipClassId;
  faction: DevFaction;
  /** The striking ship's speed, m/s (ours when we ram; the closing speed for a bump). */
  speed: number;
  /** Where the blow lands on the ship struck: section 0 (stern) .. 4 (bow), and side. */
  section: number;
  side: 1 | -1;
  /** Seconds before the blow. */
  lead: number;
}

export interface ExplodeSpec {
  /** On board a ship, in the water off her side, or at a bearing from us. */
  where: 'ship' | 'alongside' | 'sea';
  target: number;
  section: number;
  level: 'hold' | 'deck' | 'rail';
  side: -1 | 0 | 1;
  /** Off her side (alongside), or from us (sea), m; and the bearing from our bow. */
  distance: number;
  bearing: number;
  /** A shell bursting is 1; a magazine going up is 6. */
  power: number;
  count: number;
  interval: number;
}

export type DamageSpec =
  | { part: 'hull'; section: number; side: 1 | -1; percent: number }
  | { part: 'hole'; section: number; side: 1 | -1; height: 'below' | 'waterline' | 'above'; radius: number }
  | { part: 'rail'; section: number; side: 1 | -1; size: number }
  | { part: 'deck'; section: number; deck: number; frames: number }
  | { part: 'mast'; mast: MastId; percent: number; rigging: number }
  | { part: 'rudder'; percent: number }
  | { part: 'sails'; sail: string; radius: number; burn: number }
  | { part: 'guns'; side: -1 | 0 | 1; count: number }
  | { part: 'crew'; killed: number; wounded: number; morale: number | null }
  | { part: 'flood'; section: number; fraction: number }
  | { part: 'fire'; section: number; level: 0 | 1; heat: number }
  | { part: 'magazine'; heat: number };

export type DestroyKind = 'dismast' | 'rudder' | 'magazine' | 'back' | 'stove' | 'strike' | 'capture' | 'sink' | 'burn' | 'guns' | 'sails' | 'crew';

export interface SeaSpec {
  /** Wind-sea height relative to the weather's; null: as the weather has it. */
  scale: number | null;
  /** Swell significant height, m; null: the weather's. */
  swell: number | null;
  /** Crest sharpness 0..1; null: the weather's. */
  chop: number | null;
  /** Wind speed, m/s; null: the weather's. */
  wind: number | null;
  /** Where the wind comes from, off our bow (radians); null: the weather's. */
  windRel: number | null;
  /** Jump straight to the new sea (else it builds or dies away over a minute or two). */
  now: boolean;
}

export type CombatScenario = 'duel' | 'broadside' | 'boardUs' | 'ramUs' | 'chase' | 'hunted' | 'squadron' | 'ambush' | 'convoy' | 'melee';

export interface CombatSpec {
  scenario: CombatScenario;
  cls: ShipClassId;
  faction: DevFaction;
  count: number;
  range: number;
  difficulty: DifficultyId;
  /** Beat our own people to quarters at once. */
  quarters: boolean;
}

export type FleetOrder = 'fight' | 'board' | 'hold' | 'flee' | 'strike' | 'release';

export type CleanupKind = 'dev' | 'others' | 'shot' | 'wreckage' | 'fires' | 'repair' | 'sea' | 'all';

export type DevAction =
  | { kind: 'spawn'; spec: SpawnSpec }
  | { kind: 'merge'; spec: MergeSpec }
  | { kind: 'mergeOrder'; order: MergeOrder; a: number; b: number }
  | { kind: 'fire'; spec: FireSpec }
  | { kind: 'battery'; ammo: AmmoId; side: -1 | 0 | 1 }
  | { kind: 'collide'; spec: CollideSpec }
  | { kind: 'explode'; spec: ExplodeSpec }
  | { kind: 'damage'; target: number; spec: DamageSpec }
  | { kind: 'destroy'; target: number; what: DestroyKind; mast: MastId; level: number; side: 1 | -1; section: number }
  | { kind: 'sea'; spec: SeaSpec }
  /** Turn our ship so the sea comes from `rel` off her bow (0 head sea, pi following). */
  | { kind: 'face'; rel: number }
  | { kind: 'combat'; spec: CombatSpec }
  | { kind: 'orders'; who: 'dev' | 'all' | number; order: FleetOrder }
  | { kind: 'cleanup'; what: CleanupKind; target: number };

/** What the boat-merging test has seen so far. */
export interface MergeWatch {
  /** The boarder and the ship she's to grapple (vessel ids). */
  a: number;
  b: number;
  /** Sim times: set up, lines across, hand-to-hand begun, over (-1 until then). */
  start: number;
  lashed: number;
  fight: number;
  ended: number;
  /** Water between the hulls and speed between the ships as the lines went across. */
  lashGap: number;
  lashRel: number;
  /** Closest the hulls have come, m of water. */
  minGap: number;
  /** Times the lines have parted or been cut. */
  parted: number;
  /** Collisions between the two, and the hardest (0..1, as the event reports it). */
  collisions: number;
  hardest: number;
  /** Men each had lost before the test (the watch counts from there). */
  killedA: number;
  killedB: number;
  /** How it ended, once it has. */
  outcome: string;
}

const KNOTS = 1.9438;
const STEM = new Vec3(16.6, 0.4, 0);

/** Middle of hull section `i` (0 stern .. 4 bow), design x, kept where the hull has breadth. */
export function sectionMid(i: number): number {
  const s = clamp(Math.round(i), 0, 4);
  return clamp((SECTION_BOUNDS[s]! + SECTION_BOUNDS[s + 1]!) / 2, -15.5, 15.5);
}

/**
 * Lay a gun at `from` on world point `to`: the muzzle velocity that carries a
 * ball of this ammunition there through the air as the sim flies it (drag,
 * gravity and the wind), on the direct trajectory. Null if it can't reach
 * (shot is only followed for 14 s, so lobs never can). `tof` is the time of
 * flight, s.
 */
export function layGun(from: Vec3, to: Vec3, ammo: AmmoId, weight: number, windX: number, windZ: number, dt: number): { vel: Vec3; tof: number } | null {
  const a = AMMO[ammo];
  const k = a.drag / Math.cbrt(weight);
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const R = Math.hypot(dx, dz);
  if (R < 1) return null;
  const lx = dx / R, lz = dz / R;
  let ax = lx, az = lz;
  // Where the ball crosses the mark's range: how far above it, how far to
  // one side, and when. Integrated exactly as Ballistics does.
  const fly = (e: number): { h: number; side: number; t: number } | null => {
    let px = 0, py = 0, pz = 0;
    let vx = ax * Math.cos(e) * a.speed, vy = Math.sin(e) * a.speed, vz = az * Math.cos(e) * a.speed;
    for (let t = dt; t < 14; t += dt) {
      const rx = vx - windX, rz = vz - windZ;
      const f = k * Math.hypot(rx, vy, rz) * dt;
      vx -= rx * f;
      vy -= vy * f + 9.81 * dt;
      vz -= rz * f;
      const qx = px, qy = py, qz = pz;
      px += vx * dt;
      py += vy * dt;
      pz += vz * dt;
      const along = px * lx + pz * lz;
      if (along >= R) {
        const before = qx * lx + qz * lz;
        const u = (R - before) / Math.max(1e-9, along - before);
        const sx = qx + (px - qx) * u, sz = qz + (pz - qz) * u;
        return { h: qy + (py - qy) * u - dy, side: -sx * lz + sz * lx, t: t - dt + u * dt };
      }
      if (vy < 0 && py < dy - 80) return null;
    }
    return null;
  };
  const h = (e: number): number => fly(e)?.h ?? -1e9;
  let best: { e: number; t: number } | null = null;
  for (let pass = 0; pass < 3; pass++) {
    // The lowest elevation that brings the ball down on the mark.
    let root = NaN;
    let e0 = -1.2, h0 = h(e0);
    for (let e1 = -1.18; e1 <= 1.2 && Number.isNaN(root); e1 += 0.02) {
      const h1 = h(e1);
      if (h0 < 0 && h1 >= 0) {
        let lo = e0, hi = e1;
        for (let i = 0; i < 24; i++) {
          const mid = (lo + hi) / 2;
          if (h(mid) < 0) lo = mid;
          else hi = mid;
        }
        root = hi;
      }
      e0 = e1;
      h0 = h1;
    }
    if (Number.isNaN(root)) return null;
    const e = root;
    const r = fly(e);
    if (!r) return null;
    best = { e, t: r.t };
    // The wind sets her off to one side: lay off for it and try again.
    if (Math.abs(r.side) < 0.03) break;
    const turn = -r.side / R, c = Math.cos(turn), s = Math.sin(turn);
    [ax, az] = [ax * c - az * s, ax * s + az * c];
  }
  if (!best) return null;
  const v = new Vec3(ax * Math.cos(best.e), Math.sin(best.e), az * Math.cos(best.e)).scale(a.speed);
  return { vel: v, tof: best.t };
}

/** "from the starboard bow", "from ahead" and so on (where the wind comes from). */
function fromWords(rel: number): string {
  return `from ${relWords(rel).replace(/^on the /, 'the ').replace('dead ahead', 'ahead').replace('right astern', 'astern')}`;
}

/** "on the starboard bow" and so on, for a bearing off a ship's bow. */
export function relWords(rel: number): string {
  const d = (((rel * 180) / Math.PI) % 360 + 360) % 360;
  const side = d < 180 ? 'starboard' : 'port';
  const a = d <= 180 ? d : 360 - d;
  return a < 12 ? 'dead ahead' : a < 70 ? `on the ${side} bow` : a < 110 ? `on the ${side} beam` : a < 168 ? `on the ${side} quarter` : 'right astern';
}

const AI_WORDS: Record<DevAi, string> = {
  off: 'nobody in command (she holds her course and never fires)',
  patrol: 'about her own business',
  fight: 'told where we are, and free to fight',
  board: 'coming to board',
  ram: 'coming to ram',
  flee: 'running',
  hold: 'holding her course and her fire',
};

const AIM_WORDS: Record<AimPoint, string> = {
  waterline: 'waterline', hull: 'side between wind and water', rail: 'rail and gun deck', deck: 'deck',
  mast: 'mast', rigging: 'rigging and canvas', rudder: 'rudder',
};

const pounder = (w: number): string => `${Math.round(w * 12)}-pounder`;
const kn = (ms: number): string => `${(ms * KNOTS).toFixed(1)} kn`;
const sideName = (s: number): string => (s > 0 ? 'starboard' : 'port');
const wrapTau = (a: number): number => ((a % TAU) + TAU) % TAU;
const mastSpec = (id: MastId): MastSpec => MASTS.find((m) => m.id === id)!;

/** The mast of hers nearest design x. */
function nearestMast(v: Vessel, x: number): MastSpec | null {
  let best: MastSpec | null = null;
  for (const m of MASTS) {
    if (!v.cls.masts.includes(m.id)) continue;
    if (!best || Math.abs(m.x - x) < Math.abs(best.x - x)) best = m;
  }
  return best;
}

/** Design-space point a gun is laid on. `side` is the side facing the gun (0 from ahead or astern). */
function aimPoint(v: Vessel, aim: AimPoint, section: number, side: number): Vec3 {
  const xs = sectionMid(section);
  const at = (y: number) => new Vec3(xs, y, side * halfBreadthXY(xs, y));
  switch (aim) {
    case 'waterline': return at(0.3);
    case 'hull': return at((0.3 + deckY(xs)) / 2);
    case 'rail': return at(Math.min(deckY(xs) + 0.8, sheerY(xs) - 0.1));
    case 'deck': return new Vec3(xs, deckY(xs) + 0.3, 0);
    case 'mast': {
      const m = nearestMast(v, xs);
      return m ? new Vec3(m.x, m.deckY + 5, 0) : at(0.3);
    }
    case 'rigging': {
      const m = nearestMast(v, xs);
      return m ? new Vec3(m.x, m.lowerTop - 3, 0) : at(0.3);
    }
    case 'rudder': return new Vec3(sternX(0) - 0.6, -0.4, 0);
  }
}

export class DevTools {
  /** Ships the tools put on the water (cleanup takes them off again). */
  readonly spawned = new Set<number>();
  /** The boat-merging test being watched, if any. */
  merge: MergeWatch | null = null;
  private readonly rng: Rng;
  private readonly queue: { at: number; run: () => void }[] = [];

  constructor(private readonly world: World, seed: number, private readonly dt: number) {
    this.rng = new Rng(seed);
  }

  apply(a: DevAction): void {
    switch (a.kind) {
      case 'spawn': return this.spawn(a.spec);
      case 'merge': return this.setupMerge(a.spec);
      case 'mergeOrder': {
        const x = this.find(a.a), y = this.find(a.b);
        if (!x || !y || x === y) return this.say('Pick two ships afloat to merge.', false);
        return this.mergeOrder(a.order, x, y);
      }
      case 'fire': return this.fire(a.spec);
      case 'battery': return this.battery(a.ammo, a.side);
      case 'collide': return this.collide(a.spec);
      case 'explode': return this.explode(a.spec);
      case 'damage': {
        const v = this.find(a.target);
        return v ? this.damage(v, a.spec) : this.say('No ship to damage.', false);
      }
      case 'destroy': {
        const v = this.find(a.target);
        return v ? this.destroy(v, a) : this.say('No ship to wreck.', false);
      }
      case 'sea': return this.sea(a.spec);
      case 'face': return this.face(a.rel);
      case 'combat': return this.combat(a.spec);
      case 'orders': return this.orders(a.who, a.order);
      case 'cleanup': return this.cleanup(a.what, a.target);
    }
  }

  /** Scheduled tools (rounds fired in turn, repeated blasts) and the merge watch. */
  update(dt: number): void {
    if (this.queue.length) {
      const t = this.world.time;
      for (let i = 0; i < this.queue.length;) {
        const q = this.queue[i]!;
        if (q.at > t) {
          i++;
          continue;
        }
        this.queue.splice(i, 1);
        q.run();
      }
    }
    if (this.merge) this.watchMerge();
    void dt;
  }

  /** Events of the last step (the merge watch counts collisions and captures). */
  observe(events: readonly SimEvent[]): void {
    const m = this.merge;
    if (!m || m.ended >= 0) return;
    const a = this.find(m.a), b = this.find(m.b);
    for (const e of events) {
      if (e.type === 'collision' && a && b) {
        const reach = 5.5 * (a.ship.sz + b.ship.sz) + 12;
        const near = (v: Vessel) => Math.hypot(e.x - v.ship.com.x, e.z - v.ship.com.z) < reach;
        if (near(a) && near(b)) {
          m.collisions++;
          m.hardest = Math.max(m.hardest, e.energy);
        }
      } else if (e.type === 'captured' && (e.vessel === m.a || e.vessel === m.b)) {
        m.ended = this.world.time;
        m.outcome = `${this.name(e.vessel)} taken by ${this.name(e.by)}`;
      } else if (e.type === 'shipSinking' && (e.vessel === m.a || e.vessel === m.b)) {
        m.ended = this.world.time;
        m.outcome = `${this.name(e.vessel)} going down`;
      }
    }
    // Done: a captain told to board goes back to his own judgement.
    if (m.ended >= 0) for (const v of [a, b]) if (v?.captain?.forced === 'board') v.captain.forced = null;
  }

  // ------------------------------------------------------------ helpers

  private say(text: string, ok = true): void {
    this.world.events.push({ type: 'dev', text, ok });
  }

  private find(id: number): Vessel | null {
    return this.world.vessels.find((v) => v.id === id && v.alive) ?? null;
  }

  private name(id: number): string {
    return this.world.vessels.find((v) => v.id === id)?.name ?? 'a ship';
  }

  /**
   * The nearest heading to `h` she can sail on: never nearer the wind than
   * close-hauled, or she'd lie in irons and never come round to fight.
   */
  private sailable(h: number): number {
    const from = this.world.wind.fromBearing;
    const off = wrapAngle(h - from);
    return Math.abs(off) >= 1.15 ? h : from + (off < 0 ? -1.15 : 1.15);
  }

  /** Run now, or `delay` sim seconds from now. */
  private later(delay: number, run: () => void): void {
    if (delay <= 0) run();
    else this.queue.push({ at: this.world.time + delay, run });
  }

  /** A new ship with her centre at (x, z) on `heading`, riding the sea there, making `speed`. */
  private build(cls: ShipClassId, faction: DevFaction, x: number, z: number, heading: number, speed: number, plan: number, difficulty: DifficultyId = 'normal'): Vessel {
    const w = this.world;
    const c = SHIP_CLASSES[cls];
    const v = new Vessel(c, faction, w.encounters.name(faction), x, z, heading, this.rng.int(1 << 30), difficulty, clamp(Math.round(plan), 0, c.plans.length - 1));
    const s = v.ship;
    s.translate(x - s.com.x, w.waves.heightAt(x, z), z - s.com.z);
    s.vel.set(Math.sin(heading) * speed, 0, -Math.cos(heading) * speed);
    w.vessels.push(v);
    this.spawned.add(v.id);
    return v;
  }

  /** The ship whose hull a new one's would sit inside (by more than a hand's breadth), worst first. */
  private blocker(v: Vessel): { by: Vessel; depth: number } | null {
    let worst: { by: Vessel; depth: number } | null = null;
    for (const o of this.world.vessels) {
      if (o === v || !o.alive) continue;
      const depth = hullOverlap(v, o);
      if (depth > 0.2 && (!worst || depth > worst.depth)) worst = { by: o, depth };
    }
    return worst;
  }

  /**
   * Two hulls put down inside each other burst apart at fifty knots, so a
   * new ship slides out along her bearing from us until she's clear of
   * every hull. Returns how far she was moved, m.
   */
  private keepClear(v: Vessel): number {
    const me = this.world.player.ship.com;
    let moved = 0;
    for (let i = 0; i < 80 && this.blocker(v); i++) {
      const dx = v.ship.com.x - me.x, dz = v.ship.com.z - me.z, d = Math.hypot(dx, dz) || 1;
      v.ship.translate((dx / d) * 25, 0, (dz / d) * 25);
      moved += 25;
    }
    return moved;
  }

  /**
   * A ship placed exactly (alongside, on a collision course) can't be slid
   * aside. Ships left from earlier tests in her way are taken off the water
   * (except those in `keep`); anything else in the way and she isn't put
   * there at all. Returns false if refused.
   */
  private placeExactly(v: Vessel, keep: readonly Vessel[] = []): boolean {
    for (let i = 0; i < 8; i++) {
      const b = this.blocker(v);
      if (!b) return true;
      if (this.spawned.has(b.by.id) && !keep.includes(b.by)) {
        this.remove((o) => o === b.by);
        this.say(`${b.by.name} (left from an earlier test) taken off the water to make room.`);
        continue;
      }
      this.remove((o) => o === v);
      const whose = b.by === this.world.player ? 'our' : `${b.by.name}'s`;
      this.say(`Can't put ${v.name} there: she'd be ${b.depth.toFixed(1)} m inside ${whose} hull. Open the gap, or clear the area first.`, false);
      return false;
    }
    return true;
  }

  /** Move a new ship so her design point `p` is over world (x, z) after `t` s of her present way. */
  private arrive(v: Vessel, p: Vec3, x: number, z: number, t: number): void {
    const s = v.ship;
    const now = s.designToWorld(new Vec3().copy(p), new Vec3());
    s.translate(x - s.vel.x * t - now.x, 0, z - s.vel.z * t - now.z);
  }

  /** Only this fraction of her company aboard (a ship just built, before she's drawn). */
  private man(v: Vessel, fraction: number): void {
    const f = clamp(fraction, 0.05, 1);
    if (f >= 1) return;
    const c = v.crew;
    c.reserve = Math.round(c.reserve * f);
    c.members.length = Math.min(c.members.length, Math.max(1, Math.round(v.cls.deckCrew * f)));
  }

  /** Strike down `n` of her fit men (`amount` 2 kills, 0.7 wounds), deck first, then from below. */
  private casualties(v: Vessel, n: number, amount: number): number {
    const fit = v.crew.members.filter((m) => m.alive && m.onBoard && m.health > 0.4);
    let done = 0;
    while (done < n && fit.length) {
      const m = fit.splice(this.rng.int(fit.length), 1)[0]!;
      v.crew.injure(m, amount, v.hitCtx(this.world.events));
      done++;
    }
    if (done < n && amount >= 1) {
      const below = Math.min(v.crew.reserve, n - done);
      v.crew.reserve -= below;
      v.crew.killed += below;
      done += below;
    }
    return done;
  }

  /** Put her under command (or take her captain away). `foe` is who she's told about. */
  private command(v: Vessel, ai: DevAi, difficulty: DifficultyId, foe: Vessel | null): void {
    if (ai === 'off') {
      v.captain = null;
      v.battle = false;
      return;
    }
    let c = v.captain;
    if (!c) {
      c = v.captain = new Captain(v, difficulty, this.rng.int(1 << 30));
      // Her own business: a wide loop ahead of where she is.
      const s = v.ship, h = s.heading, r = 2500;
      const cx = s.com.x + Math.sin(h) * r, cz = s.com.z - Math.cos(h) * r;
      const a0 = Math.atan2(s.com.z - cz, s.com.x - cx);
      for (let k = 1; k <= 6; k++) c.route.push({ x: cx + Math.cos(a0 + (k * TAU) / 6) * r, z: cz + Math.sin(a0 + (k * TAU) / 6) * r });
    }
    c.forced = ai === 'board' ? 'board' : ai === 'ram' ? 'ram' : ai === 'flee' ? 'flee' : ai === 'hold' ? 'idle' : null;
    if (ai !== 'patrol' && foe) c.know(foe, this.world.time);
  }

  private tearAt(c: Cloth, r: number): void {
    const i = this.rng.int(c.count);
    c.tear(c.pos[i * 3]!, c.pos[i * 3 + 1]!, c.pos[i * 3 + 2]!, r);
  }

  /** Water in her hold: `fraction` of what would take her down (1 and she founders). */
  private flood(v: Vessel, fraction: number): void {
    const s = v.ship;
    let cap = 0;
    for (const c of s.compartments) cap += c.capacity;
    const want = Math.max(cap - s.displacement, 1) * fraction;
    for (const c of s.compartments) c.water = Math.min(c.capacity, Math.max(c.water, (c.capacity * want) / cap));
  }

  /** Carry away a mast at `level` (0 the lower mast, 1 the topmast, 2 the topgallant). */
  private dismast(v: Vessel, id: MastId, level: number, side: number): string {
    const md = v.damage.masts[id];
    if (!md) return `${v.name} has no ${id} mast`;
    const spec = mastSpec(id);
    if (level === 2 && !v.cls.sails.some((s) => s.startsWith(id) && s.includes('topgallant'))) return `${v.name} carries no ${id} topgallant`;
    const lost = level === 0 ? 3 : level === 1 ? 2 : 1;
    if (md.lostLevel >= lost) return `${v.name}'s ${id} ${['mast', 'topmast', 'topgallant'][level]} is already gone`;
    const yd = level === 0 ? spec.deckY + 3 : level === 1 ? (spec.lowerTop + spec.topmastTop) / 2 : spec.topmastTop + 1;
    const where = v.ship.designToWorld(new Vec3(spec.x, yd, 0), new Vec3());
    v.onMastHit(id, 1e6, yd, side, where, this.world.events);
    return '';
  }

  /** Taken as a prize by `by` (or struck and boarded, as the melee does it). */
  private capture(v: Vessel, by: Vessel | null): void {
    const w = this.world;
    castOff(v, w.vessels);
    v.state = 'captured';
    v.battle = false;
    v.ship.sails.struck = true;
    v.ship.sails.setPlan(0);
    w.events.push({ type: 'captured', vessel: v.id, by: by?.id ?? -1 });
  }

  /** The state she's put on the water in. */
  private condition(v: Vessel, c: DevCondition): void {
    const ev = this.world.events;
    const d = v.damage;
    if (c === 'battered' || c === 'crippled') {
      for (let sec = 0; sec < 5; sec++) for (const side of [-1, 1]) d.damageHull(sec, side, d.hullMax * this.rng.range(0.3, 0.55), null);
      for (let i = 0; i < 5; i++) {
        const x = this.rng.range(-13, 13), y = this.rng.range(-0.2, 1.4), hb = halfBreadthXY(x, y);
        if (hb > 0) d.addHole(x, y, (this.rng.chance(0.5) ? 1 : -1) * hb, this.rng.range(0.07, 0.14));
      }
      for (let i = 0; i < 3; i++) d.breachRail(this.rng.range(-12, 12), this.rng.chance(0.5) ? 1 : -1, 1);
      for (const s of v.ship.sails.sails) for (let i = 0; i < 3; i++) this.tearAt(s.cloth, 0.7);
      for (const m of Object.values(d.masts)) d.damageRigging(m.id, 0.3);
      this.casualties(v, Math.round(v.cls.deckCrew * 0.15), 2);
    }
    if (c === 'crippled') {
      this.dismast(v, v.cls.masts.includes('main') ? 'main' : v.cls.masts[0]!, 0, 1);
      d.rudder = 0.15;
      this.flood(v, 0.3);
    }
    if (c === 'struck') v.strike(ev);
    if (c === 'burning') {
      for (let i = 0; i < 3; i++) {
        const x = this.rng.range(-10, 10);
        v.fire.ignite(x, deckY(x) + 0.5, this.rng.range(-2, 2), 0.6, v.ship, ev, v.id);
      }
    }
    if (c === 'sinking') this.flood(v, 1.02);
  }

  // ------------------------------------------------------------ ship spawning

  private spawn(s: SpawnSpec): void {
    const w = this.world, me = w.player.ship;
    const n = clamp(Math.round(s.count), 1, 8);
    const b = me.heading + s.bearing;
    const cx = me.com.x + Math.sin(b) * s.range, cz = me.com.z - Math.cos(b) * s.range;
    const toUs = vecToBearing(me.com.x - cx, me.com.z - cz);
    // Toward or away from us: as near that as she can sail. A heading given outright is kept, wind or no.
    const h = s.course === 'toward' ? this.sailable(toUs) : s.course === 'away' ? this.sailable(toUs + Math.PI) : me.heading + s.turn;
    const irons = Math.abs(wrapAngle(h - w.wind.fromBearing)) < 0.9 && s.speed > 0 && s.plan > 0;
    const made: Vessel[] = [];
    let moved = 0;
    for (let i = 0; i < n; i++) {
      // In company: astern of each other on her course, or side by side.
      const k = i - (n - 1) / 2;
      const along = s.formation === 'line' ? -k * s.spacing : 0;
      const across = s.formation === 'abreast' ? k * s.spacing : 0;
      const x = cx + Math.sin(h) * along + Math.cos(h) * across;
      const z = cz - Math.cos(h) * along + Math.sin(h) * across;
      const v = this.build(s.cls, s.faction, x, z, h, s.speed, s.plan, s.difficulty);
      moved = Math.max(moved, this.keepClear(v));
      this.man(v, s.crew);
      this.command(v, s.ai, s.difficulty, w.player);
      this.condition(v, s.condition);
      made.push(v);
    }
    const what = `${SHIP_CLASSES[s.cls].name.toLowerCase()}${n > 1 ? 's' : ''}, ${s.faction}`;
    const state = s.condition === 'fresh' ? '' : `; ${s.condition}`;
    const note = (irons ? ' Heading into the wind: she lies in irons.' : '') + (moved ? ` Moved ${moved} m further out to keep clear of other ships.` : '');
    this.say(`${made.map((v) => v.name).join(', ')} (${what}) ${Math.round(s.range)} m off ${relWords(s.bearing)}: ${AI_WORDS[s.ai]}${state}.${note}`);
  }

  // ------------------------------------------------------------ boat merging

  private setupMerge(m: MergeSpec): void {
    const w = this.world, me = w.player;
    let a: Vessel;
    if (m.pair === 'us') {
      if (!me.fighting) return this.say('We are in no state to board anyone (struck, sinking or taken).', false);
      a = me;
    } else {
      // The boarder, on our bow and well clear; her mark goes alongside her.
      const h = me.ship.heading, b = h + 0.9;
      const speed = m.speed ?? Math.max(0, me.ship.vel.x * Math.sin(h) - me.ship.vel.z * Math.cos(h));
      a = this.build(m.boarderClass, m.boarderFaction, me.ship.com.x + Math.sin(b) * 420, me.ship.com.z - Math.cos(b) * 420, h, speed, 3);
      this.keepClear(a);
    }
    const ha = a.ship.heading;
    const fx = Math.sin(ha), fz = -Math.cos(ha), sx = Math.cos(ha), sz = Math.sin(ha);
    let speed = a.ship.vel.x * fx + a.ship.vel.z * fz;
    if (m.speed !== null) {
      speed = m.speed;
      a.ship.vel.set(fx * speed, 0, fz * speed);
    }
    // Her centre abeam of the boarder's at the gap asked for, ahead or astern by the offset.
    const cls = SHIP_CLASSES[m.cls];
    const beams = 5.5 * (a.ship.sz + cls.scale.z);
    const d = Math.max(Math.abs(m.offset) + 0.5, m.gap + beams);
    const lat = Math.sqrt(Math.max(0, d * d - m.offset * m.offset)) * m.side;
    const bx = a.ship.com.x + fx * m.offset + sx * lat, bz = a.ship.com.z + fz * m.offset + sz * lat;
    const plan = Math.min(a.ship.sails.plan, cls.plans.length - 1);
    const b = this.build(m.cls, m.faction, bx, bz, ha - m.side * m.angle, Math.max(0, speed + m.relSpeed), plan);
    if (!this.placeExactly(b, [a])) {
      if (a !== me) this.remove((o) => o === a);
      return;
    }
    b.ship.vel.x -= sx * m.side * m.closing;
    b.ship.vel.z -= sz * m.side * m.closing;
    this.man(b, m.crew);
    b.crew.morale = clamp(m.morale, 0, 1);
    if (m.boarderMorale >= 0) a.crew.morale = clamp(m.boarderMorale, 0, 1);
    this.command(b, m.ai, 'normal', a);
    if (m.state === 'struck') b.strike(w.events);
    this.watch(a, b);
    const ours = a === me ? 'our' : `${a.name}'s`;
    const rel = Math.hypot(a.ship.vel.x - b.ship.vel.x, a.ship.vel.z - b.ship.vel.z);
    this.say(`Merge test: ${b.name} (${cls.name.toLowerCase()}, ${m.faction}${m.state === 'struck' ? ', struck' : ''}) ${hullGap(a, b).toFixed(1)} m off ${ours} ${sideName(m.side)} side, ${kn(rel)} between them.`);
    if (m.grapple === 'force') this.mergeOrder('grapple', a, b);
    else if (m.grapple === 'order') this.mergeOrder('order', a, b);
  }

  private watch(a: Vessel, b: Vessel): void {
    const t = this.world.time;
    const linked = a.boardingWith === b.id && b.boardingWith === a.id;
    this.merge = {
      a: a.id, b: b.id, start: t, lashed: linked ? t : -1, fight: -1, ended: -1, lashGap: linked ? hullGap(a, b) : NaN, lashRel: NaN,
      minGap: hullGap(a, b), parted: 0, collisions: 0, hardest: 0, killedA: a.crew.killed, killedB: b.crew.killed, outcome: '',
    };
  }

  private watchMerge(): void {
    const m = this.merge!;
    if (m.ended >= 0) return;
    const t = this.world.time;
    const a = this.find(m.a), b = this.find(m.b);
    if (!a || !b) {
      m.ended = t;
      m.outcome = 'One of them is no longer afloat.';
      return;
    }
    const gap = hullGap(a, b);
    m.minGap = Math.min(m.minGap, gap);
    const linked = a.boardingWith === b.id && b.boardingWith === a.id;
    if (linked && m.lashed < 0) {
      m.lashed = t;
      m.lashGap = gap;
      m.lashRel = Math.hypot(a.ship.vel.x - b.ship.vel.x, a.ship.vel.z - b.ship.vel.z);
    } else if (!linked && m.lashed >= 0) {
      // Parted or cut: a fresh grapple starts the clock again.
      m.parted++;
      m.lashed = -1;
      m.fight = -1;
    }
    if (linked && m.fight < 0 && gap <= BOARDING_GAP && (hostile(a, b) || hostile(b, a))) m.fight = t;
  }

  private mergeOrder(order: MergeOrder, a: Vessel, b: Vessel): void {
    const w = this.world, me = w.player, ev = w.events;
    if (this.merge?.a !== a.id || this.merge.b !== b.id) this.watch(a, b);
    const foes = hostile(a, b) || hostile(b, a);
    switch (order) {
      case 'grapple': {
        const gap = hullGap(a, b);
        if (gap > LINE_LENGTH) return this.say(`${b.name} is ${gap.toFixed(0)} m off: lines would part at once (they hold to ${LINE_LENGTH} m of water).`, false);
        lash(a, b, w.vessels, ev);
        return this.say(`Grapnels made fast between ${a.name} and ${b.name}, ${gap.toFixed(1)} m of water between them${foes ? '' : '; not enemies, so they only lie alongside'}.`);
      }
      case 'order': {
        const check = canGrapple(a, b);
        const why = check.why === 'far' ? `she's ${check.gap.toFixed(1)} m off (grapnels reach ${GRAPNEL_REACH} m)`
          : check.why === 'fast' ? `${kn(check.rel)} between them (the lines hold up to ${kn(GRAPNEL_SPEED)})`
          : check.why === 'prize' ? `${b.boardingWith >= 0 ? 'she is already grappled' : 'one of them is out of the fight'}` : '';
        if (a === me) {
          w.orderBoard();
          return this.say(check.ok ? `Boarding order given: grapnels away at ${b.name}.` : `Boarding order given (it stands 4 s), but ${why}.`, check.ok);
        }
        if (!foes) return this.say(`${a.name} won't board ${b.name}: they aren't enemies. "Grapple now" lashes them anyway.`, false);
        if (!b.fighting) return this.say(`Captains don't board a ship that has struck; "Grapple now" takes possession.`, false);
        this.command(a, 'board', 'normal', b);
        return this.say(`${a.name}'s captain ordered to board ${b.name}${check.ok ? '' : ` (${why} for now)`}.`, true);
      }
      case 'cut': {
        const was = a.boardingWith >= 0 || b.boardingWith >= 0;
        castOff(a, w.vessels);
        castOff(b, w.vessels);
        for (const v of [a, b]) if (v.captain?.forced === 'board') v.captain.forced = null;
        return this.say(was ? `Lines cut between ${a.name} and ${b.name}.` : `${a.name} and ${b.name} weren't grappled.`, was);
      }
      case 'strike':
        if (!b.fighting) return this.say(`${b.name} is out of the fight already (${b.state}).`, false);
        b.strike(ev);
        return this.say(`${b.name} strikes her colours.`);
      case 'capture':
        if (b.state === 'captured' || b.state === 'sinking') return this.say(`${b.name} is past taking.`, false);
        this.capture(b, a);
        return this.say(`${b.name} taken as a prize by ${a.name}.`);
      case 'weaken': {
        const fit = b.crew.members.filter((m) => m.alive && m.onBoard && m.health > 0.4).length;
        const n = this.casualties(b, Math.ceil(fit / 2), 2);
        b.crew.reserve = Math.floor(b.crew.reserve / 2);
        return this.say(`${n} of ${b.name}'s men struck down, half her people below lost.`);
      }
      case 'rally':
        a.crew.morale = 1;
        return this.say(`${a.name}'s people are in fine heart (morale 100%).`);
      case 'dishearten':
        b.crew.morale = 0.1;
        return this.say(`${b.name}'s people lose heart (morale 10%).`);
      case 'tear': {
        // Throw them apart: the haul fights it, and the lines part if the gap opens past their length.
        const dx = b.ship.com.x - a.ship.com.x, dz = b.ship.com.z - a.ship.com.z, d = Math.hypot(dx, dz) || 1;
        b.ship.vel.x += (dx / d) * 4;
        b.ship.vel.z += (dz / d) * 4;
        return this.say(`${b.name} flung clear at 4 m/s: the lines part at ${LINE_LENGTH} m of water.`);
      }
    }
  }

  // ------------------------------------------------------------ shells

  private fire(f: FireSpec): void {
    const v = this.find(f.target);
    if (!v) return this.say('No ship to fire at.', false);
    const n = clamp(Math.round(f.count), 1, 20);
    for (let i = 0; i < n; i++) this.later(i * Math.max(0, f.interval), () => this.shoot(f, i === 0, n));
  }

  /** One round from a gun standing on the water, laid on her. */
  private shoot(f: FireSpec, report: boolean, n: number): void {
    const w = this.world;
    const v = this.find(f.target);
    if (!v) return;
    const s = v.ship, ammo = AMMO[f.ammo];
    const sn = Math.sin(f.from);
    const side = sn > 0.35 ? 1 : sn < -0.35 ? -1 : 0;
    const aim = s.designToWorld(aimPoint(v, f.aim, f.section, side), new Vec3());
    const b = s.heading + f.from;
    const gun = new Vec3(aim.x + Math.sin(b) * f.range, 0, aim.z - Math.cos(b) * f.range);
    gun.y = w.waves.heightAt(gun.x, gun.z) + Math.max(1, f.height);
    const wx = w.wind.dirX * w.wind.speed, wz = w.wind.dirZ * w.wind.speed;
    // Laid on where she'll be when the ball arrives.
    let lay = layGun(gun, aim, f.ammo, f.weight, wx, wz, this.dt);
    for (let k = 0; k < 2 && lay; k++) {
      const t = lay.tof;
      lay = layGun(gun, new Vec3(aim.x + s.vel.x * t, aim.y + s.vel.y * t, aim.z + s.vel.z * t), f.ammo, f.weight, wx, wz, this.dt);
    }
    if (!lay) {
      if (report) this.say(`${ammo.name} won't carry ${f.range} m to ${v.name} (shot is followed for 14 s).`, false);
      return;
    }
    const speed = lay.vel.length();
    const dir = lay.vel.clone().scale(1 / speed);
    for (let k = 0; k < ammo.count; k++) {
      // The shake of a real gun, and the spread of a case of grape.
      const sp = ammo.spread + 0.0012;
      const d = new Vec3(dir.x + this.rng.gauss() * sp, dir.y + this.rng.gauss() * sp * 0.7, dir.z + this.rng.gauss() * sp).normalize();
      w.ballistics.launch(-1, gun, d.scale(speed * this.rng.range(0.995, 1.005)), f.ammo, f.weight);
    }
    w.events.push({ type: 'cannonFire', index: -1, x: gun.x, y: gun.y, z: gun.z, dx: dir.x, dy: dir.y, dz: dir.z, vessel: -1, weight: f.weight });
    if (!report) return;
    const at = f.aim === 'mast' || f.aim === 'rigging' || f.aim === 'rudder' ? '' : ` (${sectionName(f.section).toLowerCase()})`;
    const rounds = n > 1 ? `${n} rounds of ${ammo.name.toLowerCase()}` : ammo.name;
    const up = f.height > 30 ? `, ${Math.round(f.height)} m up (plunging)` : f.height > 8 ? `, ${Math.round(f.height)} m up` : '';
    this.say(`${rounds} from a ${pounder(f.weight)} ${f.range} m ${relWords(f.from)} of ${v.name}${up}, laid on her ${AIM_WORDS[f.aim]}${at}: ${lay.tof.toFixed(2)} s in the air.`);
  }

  /** Our own guns loaded with `ammo` at once; then a broadside if asked for. */
  private battery(ammo: AmmoId, side: -1 | 0 | 1): void {
    const me = this.world.player;
    if (!me.cls.ammo.includes(ammo)) return this.say(`${me.cls.name}s don't carry ${AMMO[ammo].name.toLowerCase()}.`, false);
    me.battery.ammo = ammo;
    let n = 0;
    for (const g of me.battery.guns) {
      if (g.destroyed) continue;
      g.charge = ammo;
      g.loaded = true;
      g.reload = 1;
      g.fireAt = -1;
      n++;
    }
    if (side !== 0) this.world.orderFire(side);
    this.say(`${n} guns loaded with ${AMMO[ammo].name.toLowerCase()}${side ? `; ${sideName(side)} broadside ordered (it goes as the crews come up)` : ''}.`);
  }

  // ------------------------------------------------------------ collisions

  private collide(c: CollideSpec): void {
    const w = this.world, me = w.player, h = me.ship.heading;
    const lead = clamp(c.lead, 1, 30);
    const xs = sectionMid(c.section);
    const onSide = () => new Vec3(xs, 0.4, c.side * halfBreadthXY(xs, 0.4));
    const where = `${sectionName(c.section).toLowerCase()}, ${sideName(c.side)} side`;
    switch (c.scenario) {
      case 'ramUs':
      case 'glancing':
      case 'headOn': {
        // Her stem meets our side (or our stem) where we'll be then.
        const hr = c.scenario === 'ramUs' ? h - (c.side * Math.PI) / 2 : c.scenario === 'glancing' ? h + Math.PI + c.side * 0.44 : h + Math.PI;
        const q = me.ship.designToWorld(c.scenario === 'headOn' ? STEM.clone() : onSide(), new Vec3());
        const r = this.build(c.cls, c.faction, q.x, q.z, hr, c.speed, 2);
        this.arrive(r, STEM, q.x + me.ship.vel.x * lead, q.z + me.ship.vel.z * lead, lead);
        if (!this.placeExactly(r)) return;
        this.command(r, 'off', 'normal', null);
        const how = c.scenario === 'ramUs' ? `square into our ${where}` : c.scenario === 'glancing' ? `a glancing blow down our ${where}` : 'bow to bow';
        return this.say(`${r.name} will strike ${how} at ${kn(c.speed)} in ~${lead.toFixed(0)} s (nobody at her helm).`);
      }
      case 'weRam': {
        me.ship.vel.set(Math.sin(h) * c.speed, 0, -Math.cos(h) * c.speed);
        const stem = me.ship.designToWorld(STEM.clone(), new Vec3());
        const tx = stem.x + me.ship.vel.x * lead, tz = stem.z + me.ship.vel.z * lead;
        const v = this.build(c.cls, c.faction, tx, tz, h + (c.side * Math.PI) / 2, 0, 0);
        this.arrive(v, onSide(), tx, tz, lead);
        if (!this.placeExactly(v)) return;
        this.command(v, 'off', 'normal', null);
        return this.say(`${v.name} lies across our bow: we strike her ${where} at ${kn(c.speed)} in ~${lead.toFixed(0)} s.`);
      }
      case 'pair': {
        // Well clear of us on our bow: one lying still, the other driving into her side.
        const b = h + c.side * 0.5;
        const px = me.ship.com.x + Math.sin(b) * 360, pz = me.ship.com.z - Math.cos(b) * 360;
        const victim = this.build(c.cls, c.faction, px, pz, h + Math.PI / 2, 0, 0);
        if (!this.placeExactly(victim)) return;
        const q = victim.ship.designToWorld(onSide(), new Vec3());
        const other: DevFaction = c.faction === 'navy' ? 'pirate' : 'navy';
        const r = this.build(c.cls, other, q.x, q.z, victim.ship.heading - (c.side * Math.PI) / 2, c.speed, 2);
        this.arrive(r, STEM, q.x, q.z, lead);
        if (!this.placeExactly(r, [victim])) {
          this.remove((o) => o === victim);
          return;
        }
        this.command(victim, 'off', 'normal', null);
        this.command(r, 'off', 'normal', null);
        return this.say(`${r.name} will strike ${victim.name}'s ${where} at ${kn(c.speed)} in ~${lead.toFixed(0)} s, ${relWords(c.side * 0.5)}.`);
      }
      case 'bump': {
        // Alongside on a parallel course, drifting in at the speed given.
        const cls = SHIP_CLASSES[c.cls];
        const closing = Math.max(0.05, c.speed);
        const sx = Math.cos(h), sz = Math.sin(h), fx = Math.sin(h), fz = -Math.cos(h);
        const lat = 5.5 * (me.ship.sz + cls.scale.z) + closing * lead;
        const off = xs * me.ship.sx;
        const v = this.build(c.cls, c.faction, me.ship.com.x + fx * off + sx * c.side * lat, me.ship.com.z + fz * off + sz * c.side * lat, h, 0, Math.min(me.ship.sails.plan, cls.plans.length - 1));
        if (!this.placeExactly(v)) return;
        v.ship.vel.set(me.ship.vel.x - sx * c.side * closing, 0, me.ship.vel.z - sz * c.side * closing);
        this.command(v, 'off', 'normal', null);
        return this.say(`${v.name} alongside to ${sideName(c.side)}, drifting in at ${kn(closing)}: contact in ~${lead.toFixed(0)} s.`);
      }
    }
  }

  // ------------------------------------------------------------ explosions

  private explode(e: ExplodeSpec): void {
    const w = this.world;
    const n = clamp(Math.round(e.count), 1, 12);
    const gap = Math.max(0, e.interval);
    const times = n > 1 ? ` ×${n}` : '';
    if (e.where === 'sea') {
      const me = w.player.ship, b = me.heading + e.bearing;
      const x = me.com.x + Math.sin(b) * e.distance, z = me.com.z - Math.cos(b) * e.distance;
      for (let i = 0; i < n; i++) {
        this.later(i * gap, () => {
          const hit = this.blast(x, z, e.power);
          if (i === 0) this.say(`Blast (power ${e.power})${times} in the water ${Math.round(e.distance)} m ${relWords(e.bearing)}${hit.length ? `: ${hit.join(', ')} shaken` : ''}.`);
        });
      }
      return;
    }
    const v = this.find(e.target);
    if (!v) return this.say('No ship for the explosion.', false);
    const xs = sectionMid(e.section);
    if (e.where === 'alongside') {
      const side = e.side || 1;
      for (let i = 0; i < n; i++) {
        this.later(i * gap, () => {
          if (!v.alive) return;
          const s = v.ship;
          const p = s.designToWorld(new Vec3(xs, 0, side * halfBreadthXY(xs, 0.2)), new Vec3());
          const ox = Math.cos(s.heading) * side, oz = Math.sin(s.heading) * side;
          const hit = this.blast(p.x + ox * e.distance, p.z + oz * e.distance, e.power);
          if (i === 0) this.say(`Blast (power ${e.power})${times} ${Math.round(e.distance)} m off ${v.name}'s ${sideName(side)} side, ${sectionName(e.section).toLowerCase()}${hit.length ? `: ${hit.join(', ')} shaken` : ''}.`);
        });
      }
      return;
    }
    const y = e.level === 'hold' ? -1.3 : e.level === 'deck' ? deckY(xs) + 0.6 : deckY(xs) + 1.1;
    const z = e.level === 'rail' ? (e.side || 1) * Math.max(0.5, deckHalfWidth(xs) - 0.2) : e.side * 1.2;
    for (let i = 0; i < n; i++) this.later(i * gap, () => v.alive && v.explode(xs, y, z, e.power, w.events));
    this.say(`Explosion (power ${e.power})${times} aboard ${v.name}: ${e.level === 'hold' ? 'in the hold' : e.level === 'deck' ? 'on deck' : 'at the rail'}, ${sectionName(e.section).toLowerCase()}${e.side ? `, ${sideName(e.side)}` : ''}.`);
  }

  /**
   * An explosion in the water at (x, z): a column of spray, a shock that
   * stoves in the hulls near it and heaves them up, and death to swimmers.
   * Returns the ships it reached.
   */
  private blast(x: number, z: number, power: number): string[] {
    const w = this.world, ev = w.events;
    const y = w.waves.heightAt(x, z);
    ev.push({ type: 'explosion', x, y, z, size: power, vessel: -1 });
    ev.push({ type: 'splash', x, y, z, size: clamp(0.8 + power * 0.5, 1, 4), kind: 'wreck' });
    const reach = 6 + power * 4;
    const hit: string[] = [];
    for (const v of w.vessels) {
      if (!v.alive) continue;
      const s = v.ship;
      // The nearest point of her side at the waterline (or right under her).
      const l = s.localToDesign(s.worldToLocal(new Vec3(x, y, z), new Vec3()), new Vec3());
      const xd = clamp(l.x, -15.5, 16);
      const side = l.z >= 0 ? 1 : -1;
      const hb = halfBreadthXY(xd, 0);
      const p = s.designToWorld(new Vec3(xd, 0, side * hb), new Vec3());
      const under = Math.abs(l.z) < hb && l.x > -16.5 && l.x < 16.5;
      const d = under ? 0 : Math.hypot(p.x - x, p.z - z);
      if (d > reach) continue;
      const f = 1 - d / reach;
      v.damage.damageHull(sectionAt(xd), side, 45 * power * f * f, ev, p);
      const hbLow = halfBreadthXY(xd, -0.6);
      if (f > 0.45 && hbLow > 0) v.damage.addHole(xd, -0.6, side * hbLow, clamp(0.24 * Math.sqrt(power) * f, 0.08, 1.2), true);
      v.crew.strike(xd, deckY(xd) + 1, side * Math.max(0.5, deckHalfWidth(xd) - 0.5), 2 + power, 0.2 * f, v.hitCtx(ev));
      // The shock heaves her up and away from it.
      const j = 0.12 * power * f * s.mass;
      const nx = d > 0.1 ? (p.x - x) / d : 0, nz = d > 0.1 ? (p.z - z) / d : 0;
      s.applyImpulse(nx * j * 0.6, j, nz * j * 0.6, p.x, p.y, p.z);
      hit.push(`${v.name} (${Math.round(d)} m)`);
    }
    for (const v of w.vessels) {
      for (const m of v.crew.members) {
        if (m.alive && !m.onBoard && Math.hypot(m.wx - x, m.wz - z) < reach * 0.8) v.crew.injure(m, 2, v.hitCtx(ev));
      }
    }
    return hit;
  }

  // ------------------------------------------------------------ damage and destruction

  private damage(v: Vessel, p: DamageSpec): void {
    const w = this.world, ev = w.events, d = v.damage, s = v.ship;
    const pct = (x: number) => clamp(x, 0, 100) / 100;
    switch (p.part) {
      case 'hull': {
        const xs = sectionMid(p.section);
        const where = s.designToWorld(new Vec3(xs, 1, p.side * halfBreadthXY(xs, 1)), new Vec3());
        d.damageHull(p.section, p.side, d.hullMax * pct(p.percent), ev, where);
        const left = d.hull[p.section]![p.side > 0 ? 1 : 0]!;
        return this.say(`${v.name}: ${sectionName(p.section).toLowerCase()} planking, ${sideName(p.side)}, down ${Math.round(p.percent)}% (now ${Math.round((left / d.hullMax) * 100)}%).`);
      }
      case 'hole': {
        const xs = sectionMid(p.section);
        const y = p.height === 'below' ? -1 : p.height === 'waterline' ? 0.4 : 1.5;
        const hb = halfBreadthXY(xs, y);
        if (hb <= 0) return this.say(`No hull there to hole on ${v.name}.`, false);
        d.addHole(xs, y, p.side * hb, p.radius);
        return this.say(`${v.name} holed ${p.height === 'below' ? 'below the waterline' : p.height === 'waterline' ? 'at the waterline' : 'above the waterline'}, ${sectionName(p.section).toLowerCase()}, ${sideName(p.side)}: radius ${p.radius.toFixed(2)} m.`);
      }
      case 'rail': {
        d.breachRail(sectionMid(p.section), p.side, p.size);
        return this.say(`${v.name}'s ${sideName(p.side)} rail stove in, ${sectionName(p.section).toLowerCase()}.`);
      }
      case 'deck': {
        d.deck[p.section]! -= 60 * v.cls.hull * pct(p.deck);
        d.structure[p.section]! -= 140 * v.cls.hull * pct(p.frames);
        d.version++;
        return this.say(`${v.name}, ${sectionName(p.section).toLowerCase()}: deck −${Math.round(p.deck)}%, frames −${Math.round(p.frames)}%.`);
      }
      case 'mast': {
        const md = d.masts[p.mast];
        if (!md || md.lostLevel >= 3) return this.say(`${v.name} has no ${p.mast} mast standing.`, false);
        if (p.rigging > 0) d.damageRigging(p.mast, pct(p.rigging));
        if (p.percent > 0) {
          const spec = mastSpec(p.mast), yd = spec.deckY + 3;
          const where = s.designToWorld(new Vec3(spec.x, yd, 0), new Vec3());
          v.onMastHit(p.mast, (md.max * pct(p.percent)) / (1.3 - md.rigging * 0.3), yd, this.rng.chance(0.5) ? 1 : -1, where, ev);
        }
        return this.say(`${v.name}'s ${p.mast} mast: −${Math.round(p.percent)}% (now ${Math.round((Math.max(0, md.hp) / md.max) * 100)}%${md.lostLevel >= 3 ? ', gone by the board' : ''}), rigging ${Math.round(md.rigging * 100)}%.`);
      }
      case 'rudder': {
        d.rudder = clamp(d.rudder - pct(p.percent), 0, 1);
        if (d.rudder <= 0.05) s.rudderJam = s.rudder;
        return this.say(`${v.name}'s rudder at ${Math.round(d.rudder * 100)}%${d.rudder <= 0.05 ? ', jammed' : ''}.`);
      }
      case 'sails': {
        const sails = s.sails.sails.filter((x) => !x.lost && (p.sail === 'all' || x.spec.id === p.sail));
        if (!sails.length) return this.say(`${v.name} has no such sail set.`, false);
        for (const x of sails) {
          if (p.radius > 0) this.tearAt(x.cloth, p.radius);
          if (p.burn > 0) x.cloth.burn(pct(p.burn), () => this.rng.next());
        }
        return this.say(`${v.name}: ${p.sail === 'all' ? 'every sail' : p.sail.replace('_', ' ')} torn${p.burn > 0 ? ` and ${Math.round(p.burn)}% burnt` : ''}; canvas at ${Math.round(s.sails.sailHealth * 100)}%.`);
      }
      case 'guns': {
        let n = 0;
        const guns = v.battery.guns.map((g, i) => ({ g, i })).filter(({ g }) => !g.destroyed && (p.side === 0 || g.spec.side === p.side));
        for (const { g, i } of guns.slice(0, Math.max(0, Math.round(p.count)))) {
          v.battery.damageGun(i, 1e6);
          const m = cannonMount(g.spec), gw = s.designToWorld(new Vec3(m.x, m.y, m.z), new Vec3());
          ev.push({ type: 'impact', x: gw.x, y: gw.y, z: gw.z, what: 'gun', energy: 1, vessel: v.id, nx: 0, ny: 1, nz: 0 });
          n++;
        }
        return this.say(`${n} of ${v.name}'s guns dismounted${p.side ? ` (${sideName(p.side)})` : ''}.`, n > 0);
      }
      case 'crew': {
        const k = this.casualties(v, Math.round(p.killed), 2);
        const hurt = this.casualties(v, Math.round(p.wounded), 0.7);
        if (p.morale !== null) v.crew.morale = clamp(p.morale, 0, 1);
        return this.say(`${v.name}: ${k} killed, ${hurt} wounded; morale ${Math.round(v.crew.morale * 100)}%.`);
      }
      case 'flood': {
        const c = s.compartments[clamp(Math.round(p.section), 0, s.compartments.length - 1)]!;
        c.water = c.capacity * clamp(p.fraction, 0, 1);
        return this.say(`${v.name}'s ${sectionName(p.section).toLowerCase()} hold ${Math.round(p.fraction * 100)}% full of water.`);
      }
      case 'fire': {
        const xs = sectionMid(p.section);
        v.fire.ignite(xs, p.level === 0 ? -1.2 : deckY(xs) + 0.5, 0, clamp(p.heat, 0.05, 1), s, ev, v.id);
        return this.say(`Fire lit in ${v.name}'s ${p.level === 0 ? 'hold' : 'deck'}, ${sectionName(p.section).toLowerCase()} (heat ${Math.round(p.heat * 100)}%).`);
      }
      case 'magazine': {
        if (d.magazine.exploded) return this.say(`${v.name}'s magazine has already gone up.`, false);
        d.magazine.heat += p.heat;
        return this.say(`${v.name}'s magazine heat ${Math.round(d.magazine.heat * 100)}%${d.magazine.heat >= 1 ? ': she blows up' : ''}.`);
      }
    }
  }

  private destroy(v: Vessel, a: Extract<DevAction, { kind: 'destroy' }>): void {
    const w = this.world, ev = w.events, d = v.damage, s = v.ship;
    const xs = sectionMid(a.section);
    switch (a.what) {
      case 'dismast': {
        const why = this.dismast(v, a.mast, a.level, a.side);
        return why ? this.say(`${why}.`, false) : this.say(`${v.name}'s ${a.mast} ${['mast', 'topmast', 'topgallant'][a.level] ?? 'mast'} goes by the board to ${sideName(a.side)}.`);
      }
      case 'rudder': {
        d.rudder = 0;
        s.rudderJam = s.rudder;
        const p = s.designToWorld(new Vec3(sternX(0) - 0.6, -0.4, 0), new Vec3());
        ev.push({ type: 'impact', x: p.x, y: p.y, z: p.z, what: 'rudder', energy: 1.5, vessel: v.id, nx: 0, ny: 1, nz: 0 });
        return this.say(`${v.name}'s rudder shot away: her helm is jammed.`);
      }
      case 'magazine':
        if (d.magazine.exploded) return this.say(`${v.name}'s magazine has already gone up.`, false);
        d.magazine.heat = Math.max(d.magazine.heat, 1);
        return this.say(`${v.name}'s magazine goes up.`);
      case 'back': {
        if (d.brokenBack) return this.say(`${v.name}'s back is already broken.`, false);
        d.structure[2] = Math.min(d.structure[2]!, -1);
        d.brokenBack = true;
        const p = s.designToWorld(new Vec3(0.5, 1, 0), new Vec3());
        ev.push({ type: 'collapse', x: p.x, y: p.y, z: p.z, what: 'keel', size: 3 });
        for (const c of s.compartments) c.water = Math.min(c.capacity, c.water + c.capacity * 0.25);
        d.version++;
        return this.say(`${v.name} breaks her back.`);
      }
      case 'stove': {
        const hb = halfBreadthXY(xs, 0.2);
        const p = s.designToWorld(new Vec3(xs, 0.2, a.side * hb), new Vec3());
        d.damageHull(a.section, a.side, d.hullMax * 1.3, ev, p);
        d.addHole(xs, 0.1, a.side * hb, 0.9);
        d.breachRail(xs, a.side, 2.5);
        ev.push({ type: 'impact', x: p.x, y: p.y, z: p.z, what: 'hull', energy: 2, vessel: v.id, nx: Math.cos(s.heading) * a.side, ny: 0, nz: Math.sin(s.heading) * a.side });
        return this.say(`${v.name}'s ${sideName(a.side)} side stove in, ${sectionName(a.section).toLowerCase()}: planking gone, a great hole at the waterline.`);
      }
      case 'strike':
        if (!v.fighting) return this.say(`${v.name} is out of the fight already.`, false);
        v.strike(ev);
        return this.say(`${v.name} strikes her colours.`);
      case 'capture': {
        if (v.state === 'captured' || v.state === 'sinking') return this.say(`${v.name} is past taking.`, false);
        const by = v === w.player ? w.vessels.find((o) => o !== v && o.alive && hostile(o, v)) ?? null : w.player;
        this.capture(v, by);
        return this.say(`${v.name} taken as a prize${by ? ` by ${by.name}` : ''}.`);
      }
      case 'sink':
        this.flood(v, 1.02);
        return this.say(`${v.name} flooded past saving: she goes down.`);
      case 'burn': {
        const hw = Math.max(0.8, deckHalfWidth(xs));
        for (const lane of [-1, 0, 1]) {
          v.fire.ignite(xs, deckY(xs) + 0.5, lane * hw * 0.6, 0.8, s, ev, v.id);
          v.fire.ignite(xs, -1.2, lane * hw * 0.6, 0.6, s, ev, v.id);
        }
        return this.say(`${v.name} set ablaze, ${sectionName(a.section).toLowerCase()}, on deck and in the hold.`);
      }
      case 'guns':
        return this.damage(v, { part: 'guns', side: a.side, count: 99 });
      case 'sails': {
        const set = s.sails.sails.filter((x) => !x.lost);
        for (const x of set) for (let i = 0; i < 6; i++) this.tearAt(x.cloth, 1.2);
        return this.say(`${v.name}'s canvas shot to rags (${set.length} sails): ${Math.round(s.sails.sailHealth * 100)}% left.`);
      }
      case 'crew': {
        const fit = v.crew.members.filter((m) => m.alive && m.onBoard && m.health > 0.4).length;
        const n = this.casualties(v, Math.ceil(fit / 2), 2);
        v.crew.reserve = Math.floor(v.crew.reserve / 2);
        return this.say(`Half ${v.name}'s people cut down (${n} on deck).`);
      }
    }
  }

  // ------------------------------------------------------------ sea

  private sea(q: SeaSpec): void {
    const w = this.world, st = w.settings;
    st.seaScale = q.scale;
    st.swellHeight = q.swell;
    st.chop = q.chop;
    st.windSpeed = q.wind;
    st.windFrom = q.windRel === null ? null : wrapTau(w.player.ship.heading + q.windRel);
    if (q.now) w.settleSea();
    const parts = [
      q.wind === null ? 'wind as the weather has it' : `wind ${kn(q.wind)}${q.windRel === null ? '' : ` ${fromWords(q.windRel)}`}`,
      q.scale === null ? '' : `wind sea ×${q.scale.toFixed(2)}`,
      q.swell === null ? '' : `swell ${q.swell.toFixed(1)} m`,
      q.chop === null ? '' : `chop ${Math.round(q.chop * 100)}%`,
    ].filter(Boolean);
    this.say(`Sea: ${parts.join(', ')}${q.now ? ' (now)' : ' (building)'}; seas ${w.waves.significantHeight.toFixed(1)} m.`);
  }

  /**
   * Turn our ship to meet the sea from `rel` off her bow. The sea keeps the
   * direction it was set running in (only its height follows the weather),
   * so a head, beam or following sea is had by turning her, not the sea.
   */
  private face(rel: number): void {
    const w = this.world, s = w.player.ship;
    const t = w.waves.seaAngle;
    const from = vecToBearing(-Math.cos(t), -Math.sin(t));
    s.turnTo(from - rel);
    w.events.push({ type: 'teleport', vessel: w.player.id, x: s.com.x, z: s.com.z, dx: 0, dy: 0, dz: 0 });
    const what = Math.abs(rel) < 0.2 ? 'a head sea' : Math.abs(rel) > Math.PI - 0.2 ? 'a following sea' : Math.abs(Math.abs(rel) - Math.PI / 2) < 0.2 ? 'the sea on the beam' : `the sea ${relWords(rel)}`;
    this.say(`${w.player.name} turned to meet ${what} (the wind sea runs from ${Math.round(((from * 180) / Math.PI + 360) % 360)}°; the swell ${Math.round((((vecToBearing(-Math.cos(w.waves.swellAngle), -Math.sin(w.waves.swellAngle)) - s.heading) * 180) / Math.PI + 540) % 360 - 180)}° off her bow).`);
  }

  // ------------------------------------------------------------ combat

  private combat(c: CombatSpec): void {
    const w = this.world, me = w.player, h = me.ship.heading;
    const n = clamp(Math.round(c.count), 1, 6);
    const made: Vessel[] = [];
    const speed = 3.5;
    const put = (cls: ShipClassId, faction: DevFaction, bearing: number, range: number, heading: 'toward' | number, ai: DevAi, v0 = speed): Vessel => {
      const b = h + bearing;
      const x = me.ship.com.x + Math.sin(b) * range, z = me.ship.com.z - Math.cos(b) * range;
      const hd = this.sailable(heading === 'toward' ? vecToBearing(me.ship.com.x - x, me.ship.com.z - z) : heading);
      const v = this.build(cls, faction, x, z, hd, v0, 3, c.difficulty);
      this.keepClear(v);
      this.command(v, ai, c.difficulty, me);
      made.push(v);
      return v;
    };
    const fan = (i: number) => (i - (n - 1) / 2) * 0.22;
    const ours = Math.max(0, me.ship.vel.x * Math.sin(h) - me.ship.vel.z * Math.cos(h));
    switch (c.scenario) {
      case 'duel': for (let i = 0; i < n; i++) put(c.cls, c.faction, 0.7 + fan(i), c.range, 'toward', 'fight'); break;
      case 'broadside': for (let i = 0; i < n; i++) put(c.cls, c.faction, Math.PI / 2 + fan(i) * 0.6, c.range, h, 'fight', ours); break;
      case 'boardUs': for (let i = 0; i < n; i++) put(c.cls, c.faction, 1.0 + fan(i), c.range, 'toward', 'board'); break;
      case 'ramUs': for (let i = 0; i < n; i++) put(c.cls, c.faction, 0.8 + fan(i), c.range, 'toward', 'ram'); break;
      case 'chase': for (let i = 0; i < n; i++) put(c.cls, c.faction, fan(i) * 0.5, c.range, h, 'flee'); break;
      case 'hunted': for (let i = 0; i < n; i++) put(c.cls, c.faction, Math.PI + fan(i) * 0.5, c.range, h, 'fight', ours + 0.8); break;
      case 'squadron': for (let i = 0; i < Math.max(2, n); i++) put(i === 0 ? c.cls : 'frigate', c.faction, 0.4 + fan(i) * 0.6, c.range + i * 120, 'toward', 'fight'); break;
      case 'ambush': for (let i = 0; i < Math.max(2, n); i++) put(c.cls, c.faction, (i % 2 ? -1 : 1) * (Math.PI / 2) + fan(i) * 0.3, c.range, 'toward', 'fight'); break;
      case 'convoy': {
        const heading = h + 0.25;
        const group: Vessel[] = [];
        for (let i = 0; i < Math.max(2, n); i++) group.push(put('merchant', 'merchant', 0.55 + i * 0.12, c.range + i * 180, heading, 'patrol', 3));
        // The escort keeps her own counsel: she guards the convoy.
        group.push(put(c.cls, c.faction === 'pirate' ? 'navy' : c.faction, 0.35, c.range * 0.8, heading, 'patrol', 3));
        const ids = group.map((v) => v.id);
        for (const v of group) {
          if (!v.captain) continue;
          v.captain.convoy = ids;
          v.captain.escort = v.faction !== 'merchant';
        }
        break;
      }
      case 'melee':
        for (let i = 0; i < Math.max(2, n); i++) {
          put(c.cls, 'pirate', i * 1.3 + 0.4, c.range, 'toward', 'fight');
          put(c.cls, 'navy', i * 1.3 + 1.05, c.range, 'toward', 'fight');
        }
        break;
    }
    // In a melee everyone knows everyone at once: the fight starts now, not
    // when they happen to spot each other.
    if (c.scenario === 'melee') {
      for (const v of made) for (const o of made) if (o !== v && v.captain && hostile(v, o)) v.captain.know(o, w.time, false);
    }
    if (c.quarters) w.quarters = 120;
    this.say(`Combat, ${c.scenario}: ${made.map((v) => `${v.name} (${v.cls.name.toLowerCase()})`).join(', ')} at ${Math.round(c.range)} m${c.quarters ? '; we beat to quarters' : ''}.`);
  }

  private orders(who: 'dev' | 'all' | number, order: FleetOrder): void {
    const w = this.world, me = w.player;
    const ships = w.vessels.filter((v) => v !== me && v.alive && (who === 'all' || (who === 'dev' ? this.spawned.has(v.id) : v.id === who)));
    if (!ships.length) return this.say('No ships to give orders to.', false);
    for (const v of ships) {
      if (order === 'strike') v.strike(w.events);
      else if (order === 'release') {
        if (v.captain) v.captain.forced = null;
      } else if (v.fighting) this.command(v, order, 'normal', me);
    }
    const words: Record<FleetOrder, string> = {
      fight: 'told where we are and free to fight', board: 'ordered to board us', hold: 'ordered to hold their course and fire',
      flee: 'ordered to run', strike: 'ordered to strike', release: 'left to their own judgement',
    };
    this.say(`${ships.length === 1 ? ships[0]!.name : `${ships.length} ships`} ${words[order]}.`);
  }

  // ------------------------------------------------------------ cleanup

  private cleanup(what: CleanupKind, target: number): void {
    const w = this.world, me = w.player;
    switch (what) {
      case 'dev': {
        const n = this.remove((v) => this.spawned.has(v.id));
        return this.say(n ? `${n} test ship${n === 1 ? '' : 's'} taken off the water.` : 'No test ships out.', n > 0);
      }
      case 'others': {
        const n = this.remove((v) => v !== me);
        return this.say(`${n} ship${n === 1 ? '' : 's'} taken off the water; the sea is ours.`);
      }
      case 'shot': {
        const n = w.ballistics.projectiles.length;
        w.ballistics.projectiles.length = 0;
        this.queue.length = 0;
        return this.say(`${n} shot in the air removed; queued rounds and blasts cancelled.`);
      }
      case 'wreckage': {
        const items = w.flotsam.items;
        const before = items.length;
        for (let i = items.length - 1; i >= 0; i--) if (items[i]!.wreck !== undefined) items.splice(i, 1);
        return this.say(`${before - items.length} pieces of wreckage cleared.`);
      }
      case 'fires': {
        let n = 0;
        for (const v of w.vessels) {
          for (const c of v.fire.cells) {
            if (c.heat > 0) n++;
            c.heat = 0;
          }
          if (!v.damage.magazine.exploded) v.damage.magazine.heat = 0;
        }
        return this.say(n ? `${n} fires put out.` : 'Nothing burning.');
      }
      case 'repair': {
        const v = this.find(target) ?? me;
        return this.say(this.repair(v));
      }
      case 'sea': {
        const st = w.settings;
        st.seaScale = st.swellHeight = st.chop = st.windSpeed = st.windFrom = null;
        return this.say('Sea and wind back to the weather (they settle over a minute or two).');
      }
      case 'all': {
        const n = this.remove((v) => this.spawned.has(v.id));
        w.ballistics.projectiles.length = 0;
        this.queue.length = 0;
        const items = w.flotsam.items;
        for (let i = items.length - 1; i >= 0; i--) if (items[i]!.wreck !== undefined) items.splice(i, 1);
        const st = w.settings;
        st.seaScale = st.swellHeight = st.chop = st.windSpeed = st.windFrom = null;
        this.merge = null;
        this.repair(me);
        return this.say(`Test area reset: ${n} test ship${n === 1 ? '' : 's'} removed, shot and wreckage cleared, sea back to the weather, ${me.name} repaired.`);
      }
    }
  }

  /** Take ships off the water (never ours); nothing is left grappled to them or chasing them. */
  private remove(pred: (v: Vessel) => boolean): number {
    const w = this.world, me = w.player;
    const gone = new Set<number>();
    for (let i = w.vessels.length - 1; i >= 0; i--) {
      const v = w.vessels[i]!;
      if (v === me || !pred(v)) continue;
      castOff(v, w.vessels);
      w.vessels.splice(i, 1);
      gone.add(v.id);
      this.spawned.delete(v.id);
    }
    for (const g of w.encounters.groups) {
      for (let i = g.vessels.length - 1; i >= 0; i--) if (gone.has(g.vessels[i]!.id)) g.vessels.splice(i, 1);
    }
    for (const v of w.vessels) {
      if (gone.has(v.boardingWith)) (v.boardingWith = -1), (v.boardingSide = 0);
      if (v.captain) for (const id of gone) v.captain.contacts.delete(id);
    }
    if (this.merge && (gone.has(this.merge.a) || gone.has(this.merge.b))) this.merge = null;
    return gone.size;
  }

  /** Everything made good that can be: hull, rig, guns, fires, water, the wounded; colours hoisted again. */
  private repair(v: Vessel): string {
    const d = v.damage, s = v.ship, c = v.cls;
    if (s.sunk) return `${v.name} is on the bottom: nothing to repair.`;
    for (const row of d.hull) row.fill(d.hullMax);
    d.deck.fill(60 * c.hull);
    d.structure.fill(140 * c.hull);
    d.cargo.fill(1);
    d.holes.length = 0;
    d.breaches.length = 0;
    d.rudder = 1;
    s.rudderJam = 0;
    for (const m of Object.values(d.masts)) {
      m.hp = m.max;
      m.rigging = 1;
      m.lostLevel = 0;
      m.stump = 0;
      m.jury = 0;
      m.wreck = 0;
    }
    d.spares = c.masts.length + 1;
    s.sails.restore(c.masts);
    for (const f of v.fallen) f.cutAway = true;
    d.wreckDrag = 0;
    d.magazine.heat = 0;
    d.magazine.exploded = false;
    d.brokenBack = false;
    d.version++;
    for (const comp of s.compartments) comp.water = 0;
    for (const cell of v.fire.cells) {
      cell.heat = 0;
      cell.fuel = 1;
      cell.wet = 0;
    }
    for (const g of v.battery.guns) {
      if (g.destroyed) (g.destroyed = false), (g.hp = 30 * Math.cbrt(c.gunWeight));
      g.loaded = true;
      g.reload = 1;
      g.fireAt = -1;
    }
    let healed = 0;
    for (const m of v.crew.members) {
      if (!m.alive || !m.onBoard || m.health >= 1) continue;
      m.health = 1;
      if (m.stance === 'down') m.stance = 'stand';
      healed++;
    }
    v.crew.morale = Math.max(v.crew.morale, 0.75);
    const was = v.state;
    if (was !== 'active') {
      v.state = 'active';
      v.sinkTime = 0;
      s.sails.struck = false;
    }
    return `${v.name} repaired: hull, masts, canvas, guns and pumps made good, fires out, ${healed} wounded back on their feet${was !== 'active' ? `; no longer ${was}` : ''}. The dead stay dead.`;
  }
}
